import * as stylex from "@stylexjs/stylex";
import { ChevronDown, X } from "lucide-react";
import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, space, text } from "@/design/tokens.stylex";

// Filter and saved-view chips (Mercury 03): one row above a table holding the
// saved view, the filters and the date. A chip names its filter and, once set,
// its value; an `onClear` adds a small ×.

const styles = {
  chip: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xsuxsxl",
    kZKoxP: "x5esxvw",
    ks0D6T: "x1va8c73",
    kg3NbH: "xtft4pz",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7 xist2jy",
    kaIpWk: "x16l1o21",
    kWkggS: "x1h1f4i9",
    kMwMTN: "xgwkh9f x176rghm",
    kGuDYH: "xtu3avg",
    k63SB2: "xk50ysn",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  active: {
    kWkggS: "xlcwlql",
    kVAM5u: "xpjhqdt xqahn3k",
    $$css: true
  },
  withClear: {
    kfmiAY: "x1ga7v0g",
    kT0f0o: "x16uus16",
    ke9TFa: "x10w94by",
    $$css: true
  },
  value: {
    k7Eaqz: "xeuugli",
    $$css: true
  },
  clear: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "x23j0i4",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xpjhqdt",
    kfmiAY: "xmwqe6e",
    kT0f0o: "xnydjy2",
    kWkggS: "xlcwlql x1f9xhvi",
    kMwMTN: "x1i6qc9y",
    $$css: true
  }
};
export function ChipRow({
  children,
  end
}: {
  children: ReactNode;
  end?: ReactNode;
}) {
  return <div className="x78zum5 x1a02dak x6s0dn4 x166cprz xeuugli">
      {children}
      {end && <div className="x78zum5 x6s0dn4 xsuxsxl xvc5jky">{end}</div>}
    </div>;
}
export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style" | "children" | "value"> {
  /** What the chip filters, e.g. "Platform". */
  label: ReactNode;
  /** The chosen value; the chip reads as set when it has one. */
  value?: ReactNode;
  icon?: ReactNode;
  /** Show a chevron: the chip opens a menu or popover. */
  menu?: boolean;
  active?: boolean;
  onClear?: () => void;
  clearLabel?: string;
  xstyle?: stylex.StyleXStyles;
}
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip({
  label,
  value,
  icon,
  menu = false,
  active = value !== undefined && value !== null && value !== "",
  onClear,
  clearLabel,
  xstyle,
  type = "button",
  ...props
}, ref) {
  const chip = <button ref={ref} type={type} {...props} {...stylex.props(recipes.bareButton, styles.chip, recipes.focusRing, recipes.transition, active && styles.active, onClear && active && styles.withClear, xstyle)}>
      {icon && <span aria-hidden className="x3nfvp2 x1i6qc9y">
          {icon}
        </span>}
      {active && value !== undefined ? <>
          <span className="x1e3avs9 xo1l8bm">{label}</span>
          <span {...stylex.props(styles.value, recipes.truncate)}>{value}</span>
        </> : <span {...stylex.props(recipes.truncate)}>{label}</span>}
      {menu && <span aria-hidden className="x3nfvp2 x1i6qc9y">
          <ChevronDown size={14} />
        </span>}
    </button>;
  if (!onClear || !active) return chip;
  return <span className="x3nfvp2 x1qjc9v5 x193iq5w">
      {chip}
      <button type="button" aria-label={clearLabel ?? `Clear ${typeof label === "string" ? label : "filter"}`} onClick={onClear} {...stylex.props(recipes.bareButton, styles.clear, recipes.focusRing)}>
        <X size={12} aria-hidden />
      </button>
    </span>;
});
export default Chip;